import { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useQueries } from '@tanstack/react-query';
import clsx from 'clsx';

import { InboxHeadFilters, MobileInboxList } from '@/components/dashboard';
import SimpleTable from '@/components/common/tables/simpleTable';
import {
	Avatar,
	Button,
	EmptyBox,
	FunnelEmptyScreen,
	Icon,
	LoaderScreen,
	Typography,
} from '@/components/common';
import { GET_INBOX_CASES, GET_INBOX_LIST } from '@/api';
import { inboxService } from '@/api/inbox';
import { useEventSource } from '@/hooks/useEventSource';
import AppRoutes from '@/const/routes';
import { Options } from '@/components/ui/combobox';
import Icons from '@/const/icons';
import { formatDateWithin24h } from '@/lib';

interface TableData {
	id: string;
	name: ReactNode;
	subject: ReactNode;
	case: ReactNode;
	isActive: boolean;
	last_message_time: string;
	ai_enabled: boolean;
}

interface Props {
	setOpenNewMessage: (value: boolean) => void;
}

export const InboxList: React.FC<Props> = ({ setOpenNewMessage }) => {
	const router = useRouter();
	const session = useSession();
	const token = session.data?.accessToken;

	const searchParams = useSearchParams();
	const params = new URLSearchParams(searchParams);

	const limit = 8;
	const page = params.get('page') ?? 1;
	const search = params.get('search') ?? '';
	const caseId = params.get('caseId') ?? '';

	const [filtersOptions, setFiltersOptions] = useState<Options[]>();

	const { data } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/inbox/subscribe?token=${token}`,
	});

	const queries = useQueries({
		queries: [
			{
				queryKey: [GET_INBOX_LIST, page, search, caseId],
				queryFn: () =>
					inboxService.getInboxList({
						page: Number(page),
						limit,
						search,
						caseId,
					}),
			},
			{
				queryKey: [GET_INBOX_CASES],
				queryFn: () => inboxService.getInboxUserCases(),
			},
		],
	});

	useEffect(() => {
		if (data) {
			queries[0].refetch();
		}
	}, [data]);

	const isLoading = queries.some((query) => query.isLoading);

	const listCases = queries[0].data?.data.items || [];
	const userCases = queries[1].data?.data.items || [];

	const total = queries[0].data?.data.total || 0;
	const pageData = queries[0].data?.data.page || 0;
	const totalItems = queries[0].data?.data.totalItems || 0;
	const totalPages = queries[0].data?.data.totalPages || 1;

	const tableData = listCases.map((item) => ({
		...item,
		name: (
			<div className="flex items-center gap-2">
				<AvatarName
					firstname={item.firstname}
					lastname={item.lastname}
					photoUrl={
						item.firstname === 'ClaimMagic'
							? Icons.hatAvatar
							: item.profile_picture
					}
					isBold={!item.is_read}
				/>
				{item.ai_enabled && (
					<Icon src={Icons.wand} className="!w-4 !h-4 text-green-60" />
				)}
			</div>
		),
		subject: (
			<Typography
				type="regular-14"
				className={clsx('line-clamp-1 ', {
					'font-semibold': !item.is_read,
				})}
				text={item.subject}
			/>
		),
		case: (
			<Typography
				type="regular-14"
				className={clsx('opacity-60 line-clamp-1', {
					'font-semibold': !item.is_read,
				})}
				text={
					item.case_name === 'No case assigned' || !item.case_name
						? '-'
						: item.case_name
				}
			/>
		),
		isActive: !item.is_read,
		ai_enabled: item.ai_enabled,
	}));

	const loading = isLoading;

	const onClickRow = (rowValue: TableData, index: number): void => {
		router.push({
			pathname: `${AppRoutes.CLIENTAPP_INBOX}/${rowValue.id}`,
			query: { page: pageData, limit, index, ai_enabled: rowValue.ai_enabled },
		});
	};

	useEffect(() => {
		if (userCases && userCases.length > 0) {
			const parsedCases = userCases.map((item) => ({
				value: item.case.caseId,
				label: item.case.caseName,
			}));
			setFiltersOptions(parsedCases);
		}
	}, [userCases]);
	const tableEmptyComponent =
		!loading && tableData.length === 0 ? (
			caseId ? (
				<FunnelEmptyScreen
					className="mt-[88px] text-center"
					message="There are currently no emails matching this filter."
					classNameIcon="!w-[175px] !h-[140px]  3xl:!w-[200px] 3xl:!h-[160px]"
				/>
			) : search ? (
				<div className="mt-[88px] text-center flex flex-col  items-center">
					<Icon
						src={Icons.searchCaseEmptyState}
						className="!w-[175px] !h-[140px] 3xl:!w-[200px] 3xl:!h-[160px]"
					/>
					<Typography type="regular-18" className="text-center">
						No results were found for your search.
					</Typography>
					<br />
					<Typography type="regular-18">
						Please try using different keywords.
					</Typography>
				</div>
			) : (
				<div className="mt-[88px]">
					<EmptyBox
						message="You don't have any messages in your inbox right now."
						width={175}
						height={140}
					/>
				</div>
			)
		) : null;

	return (
		<>
			<InboxHeadFilters
				total={total}
				totalItems={totalItems}
				cases={filtersOptions || []}
				setOpenNewMessage={setOpenNewMessage}
				placeHolderSelect="Cases"
			/>
			{loading && <LoaderScreen />}

			{!loading && (
				<>
					<SimpleTable<TableData>
						tableEmptyComponent={tableEmptyComponent || undefined}
						hideRowActions
						classNameContainer="!rounded-t-2xl border-none shadow-none h-4/5 hidden md:block"
						classNameHeader="grid grid-rows-3 sm:grid-rows-[auto]
							sm:grid-cols-[270px_200px_1fr_100px] 3xl:grid-cols-[370px_300px_1fr_100px]"
						classNameRows="grid grid-rows-4 sm:grid-rows-[auto] sm:grid-cols-[270px_200px_1fr_100px]
						3xl:grid-cols-[370px_300px_1fr_100px] items-center cursor-pointer"
						onClickRow={onClickRow}
						columns={[
							{
								header: '',
								content: (instance) => <>{instance.name}</>,
							},
							{
								header: 'Subject',
								content: (instance) => <>{instance.subject}</>,
							},
							{
								header: 'Case',
								content: (instance) => <>{instance.case}</>,
							},
							{
								header: 'Date',
								content: (instance) => (
									<span className="!text-black">
										{formatDateWithin24h(instance.last_message_time)}
									</span>
								),
							},
						]}
						rows={tableData as TableData[]}
						totalPages={totalPages}
						classNamePagination={'hidden md:flex'}
					/>
					<MobileInboxList tableData={listCases} />
				</>
			)}
			<div className="fixed bottom-6 right-4 flex justify-end md:hidden w-full">
				<Button
					size="medium"
					decoration="secondary"
					icon={Icons.arrowLeft}
					className="!py-4 !px-4 !w-44 bg-white shadow-[0px_4px_8px_0px_rgba(0,0,0,0.32)] md:shadow-none"
					onClick={() => setOpenNewMessage(true)}
				>
					<Typography
						type="semibold-16"
						className="flex items-center justify-center gap-3 w-full"
					>
						<Icon src={Icons.edit} className="!w-4 !h-4" />
						New Message
					</Typography>
				</Button>
			</div>
		</>
	);
};

interface AvatarNameProps {
	firstname: string;
	lastname: string;
	photoUrl: string;
	isBold: boolean;
}

const AvatarName: React.FC<AvatarNameProps> = ({
	firstname,
	lastname,
	photoUrl,
	isBold,
}) => {
	return (
		<div className="flex items-center gap-2">
			<div>
				<Avatar
					photoUrl={photoUrl}
					size="small"
					firstname={firstname}
					lastname={lastname}
				/>
			</div>
			<Typography
				type="regular-14"
				className={clsx('capitalize', {
					'font-semibold': isBold,
				})}
			>{`${firstname} ${lastname}`}</Typography>
		</div>
	);
};
